import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Container } from '@mui/material';

import BlogHeader from 'sections/blog/BlogHeader';
import ArticleTop from 'sections/blog/ArticleTop';
import ArticleSummary from 'sections/blog/ArticleSummary';
import TableOfContents from 'sections/blog/TableOfContents';
import ArticleBody from 'sections/blog/ArticleBody';
import ArticleFooter from 'sections/blog/ArticleFooter';
import RelatedArticles from 'sections/blog/RelatedArticles';
import ContactSection from 'sections/landing/ContactSection';
import Error404 from 'pages/maintenance/404';
import Loader from 'components/Loader';
import { SEO, buildBreadcrumb } from 'hooks/useSeo';
import { PageUrl } from 'const/page-url';
import { articlePath, findRelated, getLoadedArticle, hasArticle, loadArticle } from 'const/blog';
import { categoryPath, findCategoryByLabel } from 'const/blog/categories';
import { Article } from 'types/blog';
import { buildArticleJsonLd, buildFaqJsonLd } from 'const/blog/jsonLd';

// ==============================|| BLOG ARTICLE PAGE ||============================== //
//
// 記事本体(/blog/<slug>)。slug から記事データを引いて描画するだけで、
// 記事ごとのページコンポーネントは作らない。記事を増やす作業を
// 「データを1ファイル足す」だけに保つための構成。
//
// 存在しない slug のとき:
//   本番では CloudFront が S3 にファイルがないことを見て 404 を返すため、
//   通常この画面には到達しない(aws/terraform/functions/routing.js)。
//   ここで 404 を出しているのは、サイト内リンクの書き間違いや開発中の直打ちで
//   真っ白な画面になるのを防ぐための保険。
//
// 本文の読み込みについて:
//   記事の本文は全記事ぶんをまとめて持たず、開かれた1本だけを動的に読み込む
//   (src/const/blog/registry.ts)。読み込み済みなら getLoadedArticle が同期で返す。
//
//   検索やURL直打ちで来たときは、src/index.tsx が hydrateRoot の前に読み込みを
//   終わらせているため、ここでは最初から本文が揃っている(ローダーは出ない)。
//   ローダーが出るのは、サイト内リンクから記事へ移った直後のごく短い間だけ。

const BlogArticle: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    // 初期値を同期で埋めるのが肝心。プリレンダしたHTMLを引き継ぐ時点で本文が
    // 入っていないと、Reactが描くものとHTMLが食い違ってハイドレーションに失敗する。
    const [article, setArticle] = useState<Article | undefined>(() => getLoadedArticle(slug));
    const [loading, setLoading] = useState(() => !getLoadedArticle(slug) && hasArticle(slug));

    // 一覧や関連記事から遷移したとき、前のページのスクロール位置が残らないようにする。
    // components/ScrollTop はアプリ起動時に一度動くだけで、ページ遷移では動かないため、
    // 記事から記事へ移ると本文の途中から始まってしまう。
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    useEffect(() => {
        const cached = getLoadedArticle(slug);
        if (cached) {
            setArticle(cached);
            setLoading(false);
            return undefined;
        }
        if (!hasArticle(slug)) {
            setArticle(undefined);
            setLoading(false);
            return undefined;
        }
        // 読み込み中に別の記事へ移ることがあるため、戻ってきた結果を捨てられるようにする
        let alive = true;
        setLoading(true);
        loadArticle(slug).then((loaded) => {
            if (!alive) {
                return;
            }
            setArticle(loaded);
            setLoading(false);
        });
        return () => {
            alive = false;
        };
    }, [slug]);

    if (loading) {
        return <Loader />;
    }

    if (!article) {
        return <Error404 />;
    }

    const faqJsonLd = buildFaqJsonLd(article);
    const category = findCategoryByLabel(article.category);

    return (
        <React.Fragment>
            {/* タイトルにサイト名を足していないのは、記事タイトルだけで全角32文字あり、
                これ以上伸ばすと検索結果でキーワードのほうが切れてしまうため。
                サイト名はパンくずとヘッダーで伝わる */}
            <SEO
                title={article.title}
                description={article.description}
                path={articlePath(article.slug)}
                type="article"
                publishedTime={article.publishedAt}
                modifiedTime={article.updatedAt}
                jsonLd={[
                    buildBreadcrumb([
                        { name: 'お役立ち記事', path: PageUrl.Blog.urlWithSlash },
                        // 画面のパンくず(sections/blog/ArticleTop.tsx)と同じ並びにそろえる
                        ...(category ? [{ name: category.label, path: categoryPath(category.slug) }] : []),
                        { name: article.title, path: articlePath(article.slug) }
                    ]),
                    buildArticleJsonLd(article),
                    ...(faqJsonLd ? [faqJsonLd] : [])
                ]}
            />

            <BlogHeader />
            <ArticleTop article={article} />

            {/* 本文。読み進める列の幅は md(Container)に固定している。
                長文は1行が長すぎると視線が戻れなくなるため、広い画面でも広げない */}
            <Box component="article" sx={{ pb: { xs: 8, md: 10 }, bgcolor: '#fff' }}>
                <Container maxWidth="md">
                    {/* 結論 → 目次 → 本文の順。
                        先に結論を渡しておくと、1つだけ知りたい人はここで帰れて、
                        詳しく知りたい人は目次から必要な章へ飛べる */}
                    <ArticleSummary article={article} />
                    <TableOfContents sections={article.sections} />
                    <ArticleBody sections={article.sections} />
                    <ArticleFooter article={article} />
                </Container>
            </Box>

            <RelatedArticles articles={findRelated(article)} />
            <ContactSection to="/" />
        </React.Fragment>
    );
};

export default BlogArticle;
